import { createContext, useContext, useState, useCallback, useMemo } from 'react';

/**
 * Docs dock state (#1064). Holds whether the right-side reference panel is open
 * so the user-menu entry can toggle it and the app shell can shift its content
 * left to make room. Kept tiny and app-local; the docs content itself is loaded
 * from the KB package (content/knowledgeBase.js).
 */
const DocsDockContext = createContext(null);

/** Width of the docked reference panel on md+ screens (px). */
export const DOCK_WIDTH = 400;

export const DocsDockProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openDock = useCallback(() => setOpen(true), []);
  const closeDock = useCallback(() => setOpen(false), []);
  const toggleDock = useCallback(() => setOpen((v) => !v), []);

  const value = useMemo(
    () => ({ open, openDock, closeDock, toggleDock }),
    [open, openDock, closeDock, toggleDock],
  );

  return <DocsDockContext.Provider value={value}>{children}</DocsDockContext.Provider>;
};

export const useDocsDock = () => {
  const ctx = useContext(DocsDockContext);
  if (!ctx) {
    // Safe no-op outside the provider (e.g. isolated component tests) so a
    // missing provider never throws.
    return { open: false, openDock: () => {}, closeDock: () => {}, toggleDock: () => {} };
  }
  return ctx;
};

export default DocsDockContext;
